import { type ChangeEvent, type DragEvent, type FC, useId, useRef, useState } from 'react'
import classNames from 'classnames'
import { ReactComponent as UploadIllustration } from '../../assets/images/upload_consent_file.svg'
import { usePreventWindowFileDrop } from '../../common/usePreventWindowFileDrop'
import styles from './FileUpload.module.scss'

interface FileDropZoneProps {
  id?: string
  label: string
  hint?: string
  ariaDescribedBy?: string
  unavailable?: boolean
  disabled?: boolean
  size?: 'compact' | 'large'
  accept: string[]
  onFile: (file: File) => void
  onReject?: (file: File) => void
}

export const FileDropZone: FC<FileDropZoneProps> = ({
  id,
  label,
  hint,
  ariaDescribedBy,
  unavailable = false,
  disabled = false,
  size = 'compact',
  accept,
  onFile,
  onReject,
}) => {
  const hintId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const large = size === 'large'

  usePreventWindowFileDrop()

  const take = (file: File | undefined) => {
    if (!file || disabled) {
      return
    }
    if (accept.includes(file.type)) {
      onFile(file)
    } else {
      onReject?.(file)
    }
  }

  const browse = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.click()
    }
  }

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (!disabled) {
      setDragging(true)
    }
  }

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setDragging(false)
    take(event.dataTransfer.files[0])
  }

  const copy = (
    <>
      <span className={large ? 'paragraph-medium fw-bold zocks-text-gray-900' : 'paragraph-small bold'}>
        {unavailable ? 'File unavailable. Drop a new one here or click to browse' : 'Drop your file here or click to browse'}
      </span>
      {hint ? (
        <span id={hintId} className='paragraph-small zocks-text-gray-600'>
          {hint}
        </span>
      ) : null}
    </>
  )

  return (
    <>
      <input
        ref={inputRef}
        id={id ? `${id}__file-input` : undefined}
        type='file'
        accept={accept.join(',')}
        disabled={disabled}
        hidden
        onChange={(event: ChangeEvent<HTMLInputElement>) => take(event.target.files?.[0])}
      />
      <button
        id={id}
        type='button'
        className={classNames(
          'd-flex w-100 border zocks-border-dashed rounded-3',
          large ? [styles.largeDropZone, 'flex-column align-items-center py-5'] : [styles.dropZone, 'align-items-center zocks-gap-1 px-3 py-2 text-start'],
          { [styles.dragging]: dragging },
        )}
        disabled={disabled}
        aria-label={`${unavailable ? 'Replace' : 'Upload'} file for ${label}`}
        aria-describedby={[ariaDescribedBy, hint ? hintId : undefined].filter(Boolean).join(' ') || undefined}
        onClick={browse}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {large ? (
          <>
            <span className='mb-2' aria-hidden='true'>
              <UploadIllustration />
            </span>
            <span className='d-flex flex-column align-items-center gap-1'>{copy}</span>
          </>
        ) : (
          <>
            <i className={classNames(styles.icon, 'bi bi-upload zocks-text-gray-600')} aria-hidden='true' />
            <span className={classNames(styles.content, 'd-flex flex-column justify-content-center')}>{copy}</span>
          </>
        )}
      </button>
    </>
  )
}
